import { stripExtension } from '@/assets/js/Utilities';
import { readAndCompressImage } from 'browser-image-resizer';
import { v4 as uuidv4 } from 'uuid';

export default class FileLoader {
    constructor() {
        this.activeMedia = {};
        this.previousMedia = {};
        this.sampleOrigCanvas;
        this.sampleCanvas;
        this.sampleOrigCtx;
        this.sampleCtx;
        this.img;
        this.imageName = 'Original';
        this.firstImageLoaded = false;
        this.thumbnailImg;
        this.supportedFiletypes = {
            handleImageFile: ['png', 'jpg', 'jpeg', 'bmp', 'gif'],
            handleRawImageFile: [
                'cr3',
                'dc2',
                'rdc',
                'arw',
                'bay',
                'crw',
                'cr2',
                'cap',
                'dcs',
                'dcr',
                'dng',
                'drf',
                'eip',
                'erf',
                'fff',
                'iiq',
                'k25',
                'kdc',
                'mdc',
                'mef',
                'mos',
                'mrw',
                'nef',
                'nrw',
                'obm',
                'orf',
                'pef',
                'ptx',
                'pxn',
                'raf',
                'raw',
                'rwl',
                'rw2',
                'rwz',
                'sr2',
                'srf',
                'srw',
                'x3f',
                '3fr',
            ],
            handleDPXImageFile: ['dpx'],
            handleTiffImageFile: ['tiff', 'tif'],
            handleEXRImageFile: ['exr'],
            handleVideoFile: ['mp4'],
        };

        this.unsupportedFiletypes = {
            video: [
                'mov',
                'mxf',
                'avi',
                'webm',
                'mkv',
                'wmv',
                'amv',
                'mpg',
                'mpeg',
                'm4v',
            ]
        };
    }

    getImageObject(imageURL) {
        return new Promise(resolve => {
            let img = new Image();
            img.crossOrigin = 'Anonymous';
            img.src = imageURL;
            img.onload = () => {
                vm.$store.dispatch('Helpers/revokeObjectURL', imageURL);
                resolve(img);
            };
        });
    }

    // We are checking if all LUT files are loaded, so we can continue with the app.
    areRequiredFilesLoaded() {
        return new Promise(resolve => {
            if (luts.areRequiredLUTsLoaded()) resolve();
            else {
                luts.lutsLoadedInterval = setInterval(_ => {
                    if (luts.areRequiredLUTsLoaded()) {
                        clearInterval(luts.lutsLoadedInterval);
                        resolve();
                    }
                }, 200);
            }
        });
    }

    setActiveMediaURL() {
        if (this.isRawFile(this.activeMedia.type)) {
            this.activeMedia.url = URL.createObjectURL(this.activeMedia.file);
            return this.activeMedia.url;
        }

        this.activeMedia.url = URL.createObjectURL(this.activeMedia.file);
        return this.activeMedia.url;
    }

    revokeActiveMediaURL() {
        if (!this.activeMedia.url) return;

        URL.revokeObjectURL(this.activeMedia.url);
        this.activeMedia.url = null;
    }

    revokeActiveMediaRawDevelopedURL() {
        if (!this.activeMedia.rawDevelopment?.url) return;

        URL.revokeObjectURL(this.activeMedia.rawDevelopment.url);
        this.activeMedia.rawDevelopment.url = null;
    }

    resetActiveMediaRawDeveloped() {
        this.revokeActiveMediaRawDevelopedURL();

        this.activeMedia.rawDevelopment = {
            imageData: null,
            blob: null,
            type: null,
            url: null,
            values: null,
        };
    }

    resetActiveMedia() {
        this.revokeActiveMediaURL();
        this.revokeActiveMediaRawDevelopedURL();

        this.previousMedia = _.clone(this.activeMedia);

        this.activeMedia = {
            file: null,
            url: null,
            type: null,
            imageData: null,
            exif: null,
            thumbnail: null,
            rawDevelopment: {
                imageData: null,
                blob: null,
                type: null,
                url: null,
                values: null
            },
            THREE: {
                texture: null
            }
        };
    }

    async setActiveMedia(file, fileType, imageId = false) {
        this.resetActiveMedia();
        this.removeVideoComponents();

        this.activeMedia.file = file;
        this.activeMedia.type = fileType;

        if (this.isRawFile(this.activeMedia.type)) {
            UI.hideLUTtemplates();
            if (!imageId) imageId = vm.$store.state.Projects.image;

            let storeData = await imagesStore.getItem(imageId);
            if (!storeData) storeData = await imagesStore.getItem(imageId);

            let imageBlob;
            let imageExtension;

            if (storeData.rawPreview) {
                imageBlob = storeData.rawPreview;
                imageExtension = 'jpg';
            } else {
                let result = await vm.rawDevelopment.extractThumbnailFromRaw(this.activeMedia.file, false);
                if (imageId == vm.$store.state.Projects.image) {
                    return;
                }

                // for raw files without preview
                if (!result.file_output) {
                    result = await vm.rawDevelopment.extractThumbnailFromRaw(this.activeMedia.file, false, '-o 6 -h -w');
                    // result = await this.fetchDefaultThumbnail();
                }

                imageBlob = new Blob([result.file_output], { type: 'image/jpeg' });
                imageExtension = result.extension.split('.')[1];
                this.activeMedia.exif = result.exif;

                if (storeData) {
                    if (await uploadImagesStore.getItem(imageId)) {
                        await uploadImagesStore.setItem(imageId, {
                            ...storeData,
                            rawPreview: imageBlob
                        });
                    } else {
                        await imagesStore.setItem(imageId, {
                            ...storeData,
                            rawPreview: imageBlob
                        });
                    }
                }
            }

            this.activeMedia.thumbnail = true;
            this.activeMedia.rawDevelopment.values = '-T';
            this.activeMedia.rawDevelopment.blob = imageBlob;
            this.activeMedia.rawDevelopment.url = URL.createObjectURL(this.activeMedia.rawDevelopment.blob);
            this.activeMedia.rawDevelopment.type = imageExtension;

            vm.$store.dispatch('UI/setShowRawDevelopment', false);
        } else {
            vm.$store.dispatch('UI/setShowRawDevelopment', false);
        }

        this.setActiveMediaURL();
        return this.activeMedia;
    }

    async loadActiveMedia() {
        gpu.disableAllCreativePasses();

        // NOTE: we need to check if we can somehow refactor and merge gpu.initThreejs() and gpu.initNewImage()
        if (!this.firstImageLoaded) {
            this.firstImageLoaded = true;
            await gpu.initThreejs();
        } else {
            await gpu.initNewImage();
        }
    }

    removeVideoComponents() {
        $('#video').remove();
        vm.$store.dispatch('UI/setVideoLoaded', false);
        vm.$store.dispatch('UI/resetVideo');
    }

    async hasFileQuota(files) {
        let filesQuota = await vm.$store.dispatch('Projects/fetchFilesQuota');
        let uploadTotalSize = {
            bytes: 0,
            MB: 0,
        };
        files.forEach(file => (uploadTotalSize.bytes += file.size));
        uploadTotalSize.MB = (uploadTotalSize.bytes / 1000 / 1000).toFixed(2);

        if (filesQuota.data - uploadTotalSize.MB < 0) {
            window.vm.$snackbar(`You have reached your storage quota. Please upgrade your plan.`, 'error', {
                timeout: -1,
            });
            $('#import-media').val('');
            $('#import-comment-media').val('');

            return false;
        } else return true;
    }

    filterUploadingFiles(files) {
        let supportedFiles = [];
        let unsupportedFiles = [];

        Array.from(files).forEach(file => {
            let fileType = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase();
            if (this.isFiletypeSupported(fileType)) supportedFiles.push(file);
            else unsupportedFiles.push(file);
        });

        return { supportedFiles, unsupportedFiles };
    }

    filterCommentUploadingFiles(files) {
        let supportedFiles = [];
        let unsupportedFiles = [];

        files.forEach(file => {
            let fileType = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase();
            if (loader.supportedFiletypes.handleImageFile.includes(fileType)) supportedFiles.push(file);
            else unsupportedFiles.push(file);
        });

        return { supportedFiles, unsupportedFiles };
    }

    async uploadImages(commentMediaUpload = false, importList = null) {
        if (!importList) importList = [window.vm.$root.$refs.importMedia.files[0]];
        let filteredFiles = this.filterUploadingFiles(importList);

        let selectedFiles = filteredFiles.supportedFiles;
        let failedFiles = filteredFiles.unsupportedFiles;

        $('#import-media').val('');

        // Checking if we have any files to upload, after filtering them by filetype
        if (selectedFiles.length == 0) {
            if (failedFiles.length > 0) {
                vm.$store.dispatch('Projects/setFailedUploadsList', failedFiles);
                // vm.$store.dispatch("Comments/uploadingAssetLoader", false);
                UI.showModal(UI.modals.files.failedProcessing);
            }

            return;
        }

        await vm.$store.dispatch('Loader/showProgressBar', 'indeterminate');

        const file = selectedFiles[0];
        // Creating files
        let originalName = file.name.substring(0, file.name.lastIndexOf('.'));
        let timestamp = Date.now();
        let newFilename = `${originalName}.${timestamp}`;
        let fileType = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase();
        let convertedImage;
        let thumbnailBlob;
        let rawPreview;

        // converting and renaming file
        try {
            convertedImage = await this.convertImage(file, fileType, newFilename);
        } catch {
            failedFiles.push(file);
        }

        // for video and exr files, we use default thumbnail
        if (this.isVideoFile(fileType) || this.isEXRFile(fileType) || this.isTiffFile(fileType)) {
            thumbnailBlob = await this.fetchDefaultThumbnail();
        } else if (this.isRawFile(fileType)) {
            rawPreview = await vm.rawDevelopment.extractThumbnailFromRaw(convertedImage.blob);
            thumbnailBlob = rawPreview;
        } else thumbnailBlob = convertedImage.blob;

        // Compressing image to create thumbnail
        thumbnailBlob = await readAndCompressImage(thumbnailBlob, {
            quality: lutify.imageThumbnail.quality,
            maxWidth: lutify.imageThumbnail.maxWidth,
            maxHeight: lutify.imageThumbnail.maxHeight,
        });

        // renaming thumbnail blob file
        thumbnailBlob = new File([thumbnailBlob], `${originalName}.thumbnail.${timestamp}.jpg`);

        await imagesStore.setItem('media', {
            id: 'media',
            blob: convertedImage.blob,
            name: stripExtension(convertedImage.blob.name),
            sourceFileType: fileType,
            fileType: convertedImage.type,
            type: this.isVideoFile(convertedImage.type) ? 'video' : 'media',
            thumbnail: thumbnailBlob,
            rawPreview,
        });


        vm.$store.dispatch('Loader/showProgressBar', false);

        if (failedFiles.length > 0) {
            vm.$store.dispatch(
                'Projects/setFailedUploadsList',
                failedFiles
            );
            UI.showModal(UI.modals.files.failedProcessing);
        }
    }

    async fetchDefaultThumbnail() {
        let defaultsStoreData = await defaultsStore.getItem('file-thumbnail');

        if (!defaultsStoreData) {
            let response = await fetch('./assets/media/images/fylmai-site-logo-white-project.png');
            defaultsStoreData = await response.blob();
            await defaultsStore.setItem('file-thumbnail', defaultsStoreData);
        }

        defaultsStoreData.extension = '.png';
        return defaultsStoreData;
    }

    isBasicFileType(extension) {
        extension = extension.toLowerCase();

        if (this.supportedFiletypes.handleImageFile.includes(extension)) return true;
        else return false;
    }

    isVideoFile(extension) {
        return this.supportedFiletypes.handleVideoFile.includes(extension.toLowerCase());
    }

    isEXRFile(extension) {
        return this.supportedFiletypes.handleEXRImageFile.includes(extension.toLowerCase());
    }

    isRawFile(extension) {
        return this.supportedFiletypes.handleRawImageFile.includes(extension.toLowerCase());
    }

    isTiffFile(extension) {
        return this.supportedFiletypes.handleTiffImageFile.includes(extension.toLowerCase());
    }

    typeOfActiveMedia(type) {
        if (!this.activeMedia.type) return;

        let activeMediaExtension = this.activeMedia.type;
        if (type == 'exr') return this.isEXRFile(activeMediaExtension);
        if (type == 'raw') return this.isRawFile(activeMediaExtension);
        if (type == 'video') return this.isVideoFile(activeMediaExtension);
    }

    isFiletypeSupported(extension) {
        let supported = false;
        extension = extension.toLowerCase();

        for (const key in this.supportedFiletypes) {
            if (Object.prototype.hasOwnProperty.call(this.supportedFiletypes, key) && loader.supportedFiletypes[key].includes(extension)) supported = true;
        }

        return supported;
    }

    async convertImage(blob, extension, filename = 'image') {
        extension = extension.toLowerCase();

        if (
            this.supportedFiletypes.handleImageFile.includes(extension) ||
            this.supportedFiletypes.handleEXRImageFile.includes(extension) ||
            this.supportedFiletypes.handleVideoFile.includes(extension) ||
            this.supportedFiletypes.handleRawImageFile.includes(extension) ||
            this.supportedFiletypes.handleTiffImageFile.includes(extension)
        ) {
            blob = new File([blob], `${filename}.${extension}`, {
                type: blob.type,
                lastModified: blob.lastModified,
            });
            return { blob, type: extension };
        } else if (this.supportedFiletypes.handleDPXImageFile.includes(extension)) {
            let src = await this.convertDPXToSrc(blob);
            blob = this.dataURLtoFile(src, `${filename}.jpg`);
            return { blob, type: 'jpg' };
        } else UI.showFileNotSupported();
    }

    convertDPXToSrc(file) {
        return new Promise(resolve => {
            var imgheader = file.slice(0, 8192);
            var img = file.slice(8192, file.size);

            var fr = new FileReader();
            fr.onloadend = function(evt) {
                if (evt.target.readyState == FileReader.DONE) {
                    // DONE == 2
                    var dpx = new DPXFile(evt.target.result);
                    if (evt.target.result.length == 8192) {
                        let imginfo = dpx.DPXImageInfo();

                        UI.canvas.dpx.width = imginfo['width'];
                        UI.canvas.dpx.height = imginfo['height'];

                        fr.readAsBinaryString(img);
                    } else {
                        dpx.render10Bit('dpx-canvas', evt.target.result);
                        resolve(UI.canvas.dpx.toDataURL('image/jpeg', 1));
                    }
                }
            };

            fr.readAsBinaryString(imgheader);
        });
    }

    dataURLtoFile(dataurl, filename) {
        let arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    }

    // dataURLtoBlob(dataurl) {
    // 	var arr = dataurl.split(","),
    // 		mime = arr[0].match(/:(.*?);/)[1],
    // 		bstr = atob(arr[1]),
    // 		n = bstr.length,
    // 		u8arr = new Uint8Array(n);
    // 	while (n--) {
    // 		u8arr[n] = bstr.charCodeAt(n);
    // 	}
    // 	return new Blob([u8arr], { type: mime });
    // }
}
